import {SMELTER_RECIPES} from "../State/GameData";
import {SmelterState} from "../State/InventoryContext";
import {AssemblySetup} from "../Assembly/AssemblyTypes";

/**
 * Calculate total smelter output per second for a given item type
 */
export const calculateSmelterOutputPerSecond = (
    smelterState: SmelterState[],
    itemId: string
): number => {
    // Map from smelter output itemId to smelter type
    const itemIdToSmelterType: Record<string, string> = {};
    Object.entries(SMELTER_RECIPES).forEach(([smelterType, recipe]) => {
        itemIdToSmelterType[recipe.outputId] = smelterType;
    });

    const smelterType = itemIdToSmelterType[itemId];
    if (!smelterType) return 0;

    const smelterField = smelterState.find(s => s.resourceType === smelterType);
    const recipe = SMELTER_RECIPES[smelterType];
    if (!smelterField || !recipe) return 0;

    return smelterField.smelters.reduce((sum, smelter) => {
        if (!smelter.running) return sum;
        return sum + (recipe.outputQuantity / recipe.cycleTimeMs) * 1000;
    }, 0);
};

/**
 * Calculate how many setups are using a specific source item
 */
export const countSetupsUsingSource = (
    allSetups: AssemblySetup[],
    itemId: string
): number => {
    const setupsUsing = new Set<string>();
    allSetups.forEach(setup => {
        const hasSourceNode = setup.nodes.some(n => n.type === "source" && n.itemId === itemId);
        if (hasSourceNode) {
            setupsUsing.add(setup.id);
        }
    });
    return setupsUsing.size;
};

/**
 * Calculate source node provision per second (not per tick)
 */
export const calculateSourceNodeOutputPerSecond = (
    smelterState: SmelterState[],
    allSetups: AssemblySetup[],
    setupId: string,
    itemId: string
): number => {
    const totalOutputPerSecond = calculateSmelterOutputPerSecond(smelterState, itemId);
    if (totalOutputPerSecond === 0) return 0;

    const setupsUsingThisResource = countSetupsUsingSource(allSetups, itemId);
    if (setupsUsingThisResource === 0) return 0;

    // Each setup gets an equal share
    const perSetupOutputPerSecond = totalOutputPerSecond / setupsUsingThisResource;

    // Find current setup and count source nodes of this type
    const currentSetup = allSetups.find(s => s.id === setupId);
    if (!currentSetup) return 0;

    const sourceNodesInSetup = currentSetup.nodes.filter(
        n => n.type === "source" && n.itemId === itemId
    ).length;

    if (sourceNodesInSetup === 0) return 0;

    // Divide among source nodes in this setup
    return perSetupOutputPerSecond / sourceNodesInSetup;
};
